import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Phone, MessageCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { ParticlesBackground } from "@/components/effects";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";

export function CTASection() {
  const { ref, isVisible } = useScrollAnimation();
  const { t } = useTranslation('home');
  const { language } = useLanguage();

  const ArrowIcon = language === 'he' ? ArrowLeft : ArrowRight;

  return (
    <section className="relative py-16 md:py-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary-light" />
      
      {/* Particles Background - Light variant */}
      <ParticlesBackground variant="light" />
      
      {/* Animated Orbs */}
      <div className="absolute top-0 right-1/4 w-[300px] h-[300px] bg-accent/15 rounded-full blur-[100px] animate-float" />
      <div className="absolute bottom-0 left-1/4 w-[200px] h-[200px] bg-accent/10 rounded-full blur-[80px] animate-float-delayed" />

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div 
          ref={ref}
          className={`max-w-3xl mx-auto text-center animate-on-scroll ${isVisible ? 'is-visible' : ''}`}
        >
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-4 leading-tight"
          >
            {t('ctaSection.title')}
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg md:text-xl text-primary-foreground/75 mb-8 leading-relaxed max-w-xl mx-auto"
          >
            {t('ctaSection.subtitle')}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/contact">
              <Button variant="hero" className="w-full sm:w-auto group">
                <Phone className="w-5 h-5" />
                {t('ctaSection.cta')}
                <ArrowIcon className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
              </Button>
            </Link>
            <a
              href="https://wa.me/972528300088?text=שלום,%20אשמח%20לקבל%20פרטים%20נוספים"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="heroOutline" className="w-full sm:w-auto">
                <MessageCircle className="w-5 h-5" />
                {t('ctaSection.whatsapp')}
              </Button>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
